import {ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {BlockBaseComponent} from "../block-base/block-base.component";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {StoreSelectorsService} from "../../../../services";
import {IBaseBlockComponent, ISceneItemNewValues} from "../../../../interfaces";
import {actionUpdateSceneItem} from "../../../../store/actions/user.actions";
import {MatDialog} from "@angular/material/dialog";
import {TranslocoService} from "@ngneat/transloco";
import {ButtonBarComponent, IButtonBar} from "../../../../components/add-remove-bar/button-bar.component";
import {ITableListColumn} from '../../../../components/table-list-input/table-list-input.component';
import {ToastrService} from "ngx-toastr";
import {DialogAnyFeedComponent} from "../../../../components/dialog-any-feed/dialog-any-feed.component";
import {isHostDevOrLocal, Languages} from "../../../../common";
import {FieldConfig} from "../../../../interfaces/field.interface";
import * as _ from "lodash";

/**
 * Feed Type enum for RSS block component
 * - ai_feed: AI-generated feed with DataProvider.Ai structure defined
 * - rss_feed: Regular RSS feed from predefined rssLinks
 * - embedded: Embedded RSS data stored in scene
 */
type FeedType = 'ai_feed' | 'rss_feed' | 'embedded';

@Component({
    selector: 'app-scene-block-rss',
    templateUrl: './block-rss.component.html',
    styleUrls: ['./block-rss.component.scss']
})
export class BlockRssComponent extends BlockBaseComponent implements IBaseBlockComponent {

    rssEmbeddedDataSource: Array<any> = [];
    showDescriptionFontSelector = true;
    rssNames = [];
    private feedConfigInitFull: FieldConfig[] = [];  // Full feed configuration (all fields)
    feedConfigInit: FieldConfig[] = [];  // Filtered feed source configuration (visible fields only - feedType only)
    rssFeedDetailsInit: FieldConfig[] = [];  // RSS feed details (feed dropdown + url) - shown in separate section
    aiFeedConfigInit: FieldConfig[] = [];  // AI Feed configuration
    defaultValue = '';
    defaultText = '[click to edit]';
    m_selectedDisplayMode = 0;  // 0 = AI Feed, 1 = News Feed

    /**
     * Current feed type - determined by data structure
     * - ai_feed: DataProvider.Ai is defined
     * - embedded: Rss[0].rss[0].channel[0].item exists
     * - rss_feed: URL rssLinks
     */
    feedType: FeedType = 'rss_feed';
    rssLinks = {
        'https://feeds.nbcnews.com/feeds/topstories': 'US TopStories',
        'https://time.com/newsfeed/feed/': 'Time.com Top Stories',
        'https://feeds.nbcnews.com/feeds/worldnews': 'World News',
        'https://feeds.foxnews.com/foxnews/latest?format=xml': 'US General News',
        'https://www.newsweek.com/rss': 'General News',
        'https://globalnews.ca/canada/feed/': 'Canadian News',
        'https://feeds.feedburner.com/TheAustralianMediaNews': 'Australian News',
        'https://feeds.foxnews.com/foxnews/politics': 'US Politics',
        'https://globalnews.ca/politics/feed/': 'Canadian Politics',
        'https://www.standard.co.uk/news/politics/rss': 'UK Politics',
        'https://rss.nytimes.com/services/xml/rss/nyt/Business.xml': 'US Business',
        'https://www.canadianbusiness.com/feed/': 'Canadian Business',
        'https://feeds.bbci.co.uk/news/business/rss.xml': 'UK Business',
        'https://time.com/health/feed/': 'US Health',
        'https://www.thestar.com/feeds.articles.life.health_wellness.rss': 'Canadian Health',
        'https://feeds.bbci.co.uk/news/health/rss.xml': 'UK Health',
        'https://feeds.nbcnews.com/feeds/science': 'NBC Science',
        'https://tech.yahoo.com/rss': 'Technology',
        'https://www.smithsonianmag.com/rss/science-nature/': 'Science and Nature',
        'https://rss.nytimes.com/services/xml/rss/nyt/Technology.xml': 'US Technology',
        'https://feeds.arstechnica.com/arstechnica/gadgets': 'Technology Gadgets',
        'https://sports.yahoo.com/rss': 'Sports now',
        'https://rss.nytimes.com/services/xml/rss/nyt/Sports.xml': 'US Sports',
        'https://www.si.com/rss/si_topstories.rss': 'SI Today',
        'https://www.sportsnet.ca/feed/': 'Canadian Sports',
        'https://feeds.bbci.co.uk/sport/0/rss.xml?edition=uk': 'UK Sports',
        'https://variety.com/feed/': 'US Entertainment',
        'https://www.tmz.com/rss.xml': 'US TMZ',
        'https://www.cbsnews.com/latest/rss/entertainment': 'CBS Entertainment',
        'https://feeds.nbcnews.com/feeds/todayentertainment': 'Entertainment Today',
        'https://news.yahoo.com/rss/world': 'World',
        'https://news.yahoo.com/rss/sports': ' Yahoo Sports',
        'https://news.yahoo.com/rss/us': 'U.S. National',
        'https://news.yahoo.com/rss/elections': 'Elections',
        'https://news.yahoo.com/rss/politics': 'Politics News',
        'https://news.yahoo.com/rss/business': 'Business',
        'https://news.yahoo.com/rss/entertainment': 'Yahoo Entertainment',
        'https://news.yahoo.com/rss/health': 'Yahoo Health',
        'https://news.yahoo.com/rss/science': 'Science',
        'https://news.yahoo.com/rss/highestrated': 'Most Recommended',
        'https://feeds.fireside.fm/bibleinayear/rss': 'Bible in a Year',
        'https://rss.nytimes.com/services/xml/rss/nyt/Space.xml': 'Space and Beyond',
        'https://rss.nytimes.com/services/xml/rss/nyt/Science.xml': 'NYTimes Science',
        'https://rss.nytimes.com/services/xml/rss/nyt/Climate.xml': 'Climate and Environment',
        'https://rss.nytimes.com/services/xml/rss/nyt/Arts.xml': 'Art',
        'https://rss.nytimes.com/services/xml/rss/nyt/Movies.xml': 'Movies',
        'https://rss.nytimes.com/services/xml/rss/nyt/Music.xml': 'Music',
        'https://rss.nytimes.com/services/xml/rss/nyt/Theater.xml': 'Theater',
        'https://rss.nytimes.com/services/xml/rss/nyt/Television.xml': 'TV',
        'https://rss.nytimes.com/services/xml/rss/nyt/FashionandStyle.xml': 'Style and Fashion',
        'https://rss.nytimes.com/services/xml/rss/nyt/DiningandWine.xml': 'Wine and Dine',
        'https://rss.nytimes.com/services/xml/rss/nyt/Weddings.xml': 'Love and Weddings',
        'https://rss.nytimes.com/services/xml/rss/nyt/Travel.xml': 'Travel and Leisure',
        'https://rss.nytimes.com/services/xml/rss/nyt/Automobiles.xml': 'Cars, cars, cars',
        'https://rss.nytimes.com/services/xml/rss/nyt/MostViewed.xml': 'NY Times MostViewed',
        'https://content.api.nytimes.com/svc/news/v3/all/recent.rss': 'Latest News',
        'https://moxie.foxnews.com/google-publisher/us.xml': 'Foxnews US',
        'https://moxie.foxnews.com/google-publisher/world.xml': 'Foxnews World',
        'https://moxie.foxnews.com/google-publisher/sports.xml': 'Fox Sports',
        'https://moxie.foxnews.com/google-publisher/travel.xml': 'Travels',
        'https://moxie.foxnews.com/google-publisher/tech.xml': 'Fox Tech',
        'https://moxie.foxnews.com/google-publisher/health.xml': 'Health News',
        'https://moxie.foxnews.com/google-publisher/science.xml': 'Fox Science',
        'https://www.latimes.com/local/rss2.0.xml': 'LA Times',
        'https://www.politico.com/rss/politicopicks.xml': 'Politics',
        'https://cdn.feedcontrol.net/8/1114-wioSIX3uu8MEj.xml': 'Headlines',
        'http://www.ynet.co.il/Integration/StoryRss2.xml': 'ynet hebrew',
        'custom': 'custom'
    };

    group: FormGroup;
    tableButtons: IButtonBar[] = [];
    tableColumns: Array<ITableListColumn> = [];
    inDev = false;

    @ViewChild('dtable') dtable: any;
    @ViewChild('feedForm') feedForm: any;
    @ViewChild('rssFeedDetailsForm') rssFeedDetailsForm: any;
    @ViewChild('aiFeedForm') aiFeedForm: any;

    @ViewChild(ButtonBarComponent) addRemoveBarComponent: ButtonBarComponent;

    constructor(
        private translocoService: TranslocoService,
        public override storeSelector: StoreSelectorsService,
        public override dialog: MatDialog,
        private toastr: ToastrService,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef
    ) {
        super(storeSelector, dialog);
        this.tableButtons = [
            {
                callBack: v => (v),
                icon: 'add',
                name: 'add',
                disableByList: false,
                disabled: false,
                tooltip: this.translocoService.translate('general.add', {}, this.translocoService.getActiveLang())
            },
            {
                callBack: v => (v),
                icon: 'remove',
                name: 'remove',
                confirmRemove: this.translocoService.translate('toast.deleteConfirm', {}, this.translocoService.getActiveLang()),
                disableByList: true,
                disabled: false,
                tooltip: this.translocoService.translate('general.remove', {}, this.translocoService.getActiveLang())
            },
        ];
        this.tableColumns = [
            {
                inputType: 'edit',
                name: 'title',
                maxStringSize: '20'
            },
            {
                inputType: 'edit',
                name: 'description',
                maxStringSize: '30'
            },
        ];

        this.initRssNames();
        this.inDev = isHostDevOrLocal();
    }

    override onInit(): void {
        super.onInit();
        this.group = this.fb.group({
            rssEmbeddedTableControl: []
        });

        // Ensure AI feed config is initialized early
        if (this.aiFeedConfigInit.length === 0) {
            this.initAiFeedConfig();
        }
    }

    private determineFeedType(): FeedType {
        const rssValue = this.sceneItem?.Data?.[0]?.Rss?.[0]?.$?.url;
        const rssEmbedded = this.sceneItem?.Data?.[0]?.Rss[0]?.rss?.[0]?.channel?.[0]?.item;
        const dataProvider = this.sceneItem?.Data?.[0]?.DataProvider?.[0];
        const aiData = dataProvider?.Ai?.[0];

        // Check for embedded RSS first
        if (rssEmbedded) {
            return 'embedded';
        }

        // Check for AI feed (DataProvider.Ai is defined)
        if (aiData) {
            return 'ai_feed';
        }

        // Check for regular RSS feed or custom RSS
        const foundRssName = this.rssLinks[rssValue];

        if (foundRssName !== undefined) {
            return 'rss_feed';
        }

        // Default to custom RSS
        return 'rss_feed';
    }

    /**
     * Map internal feedType to UI dropdown value
     */
    private feedTypeToUiValue(feedType: FeedType): string {
        const mapping = {
            'ai_feed': 'AI feed',
            'rss_feed': 'Rss feed',
            'embedded': 'Embedded'
        };
        return mapping[feedType];
    }

    /**
     * Map UI dropdown value to internal feedType
     */
    private uiValueToFeedType(uiValue: string): FeedType {
        const mapping = {
            'AI feed': 'ai_feed',
            'Rss feed': 'rss_feed',
            'Embedded': 'embedded'
        };
        return (mapping[uiValue] || 'rss_feed') as FeedType;
    }

    override componentInit() {
        super.componentInit();

        // Determine the current feed type from data structure
        this.feedType = this.determineFeedType();

        // Set the display mode tab based on feedType
        // If AI feed, select AI Feed tab (0), otherwise select News Feed tab (1)
        this.m_selectedDisplayMode = (this.feedType === 'ai_feed') ? 0 : 1;

        const rssValue = this.sceneItem?.Data?.[0]?.Rss?.[0]?.$?.url;
        const rssEmbedded = this.sceneItem?.Data?.[0]?.Rss[0]?.rss?.[0]?.channel?.[0]?.item;
        const dataProvider = this.sceneItem?.Data?.[0]?.DataProvider?.[0];
        const aiData = dataProvider?.Ai?.[0];

        // Set UI dropdown value based on feedType
        this.setConfigValue('feed', 'feedType', this.feedTypeToUiValue(this.feedType));

        // Configure based on feed type
        switch (this.feedType) {
            case 'embedded':
                this.rssEmbeddedDataSource = rssEmbedded;
                
                this.setConfigValue('feed', 'url', 'none');
                this.setConfigValue('ui', 'minRefreshTime', '0');
                break;

            case 'ai_feed':
                // AI Feed mode - load from DataProvider.Ai structure
                
                this.setConfigValue('feed', 'url', rssValue);

                // Load AI feed configuration
                const language = aiData.$?.language || 'English';
                const prompt = aiData.Prompt?.[0] || aiData["Description"]?.[0] || '';
                const fields = aiData.Fields?.[0]?.Field || [];

                // Find title and description fields
                const titleField = fields.find(f => f.$.name === 'title');
                const descField = fields.find(f => f.$.name === 'description');

                this.setConfigValue('ai', 'aiFeedLanguage', language);
                this.setConfigValue('ai', 'requestFeed', prompt);
                this.setConfigValue('ai', 'aiFeedTitle', titleField?.$?.description || '');
                this.setConfigValue('ai', 'aiFeedDescription', descField?.$?.description || '');
                break;

            case 'rss_feed':
                // CRITICAL: Set URL first to ensure it's preserved
                this.setConfigValue('feed', 'url', rssValue);

                // CRITICAL: If URL matches a predefined feed, set the feed dropdown
                // If URL is custom (not in list), set feed dropdown to "custom"
                if (rssValue && this.rssLinks[rssValue]) {
                    const feedName = this.rssLinks[rssValue];
                    this.setConfigValue('feed', 'feed', feedName);
                } else if (rssValue) {
                    // Set the feed dropdown to "custom" for custom URLs
                    this.setConfigValue('feed', 'feed', 'custom');
                } else {
                    // No URL - clear the feed dropdown
                    this.setConfigValue('feed', 'feed', '');
                }
                break;

        }

        // Update visible fields based on feedType
        this.updateVisibleFields();

        // Trigger change detection to ensure UI updates
        this.cdr.detectChanges();
    }

    override componentStoreUpdates() {
        super.componentStoreUpdates();

        this.toggleDirection();
        const rssValue = this.sceneItem?.Data?.[0]?.Rss?.[0]?.$?.url;
        const rssEmbedded = this.sceneItem?.Data?.[0]?.Rss[0]?.rss?.[0]?.channel?.[0]?.item;

        if (rssEmbedded) {
            this.rssEmbeddedDataSource = rssEmbedded;
            this.group.get('rssEmbeddedTableControl').setValue(rssEmbedded || []);
        } else {
            this.rssEmbeddedDataSource = [];
            this.group.get('rssEmbeddedTableControl').setValue([]);

            // Only set URL if it's a string (not object or undefined)
            if (typeof rssValue === 'string') {
                this.setFormValue('url', rssValue);
                // Also update the config value to ensure consistency
                this.setConfigValue('feed', 'url', rssValue);
            }
        }

        // Set AI feed form values if we're in AI feed mode
        if (this.feedType === 'ai_feed') {
            const dataProvider = this.sceneItem?.Data?.[0]?.DataProvider?.[0];
            const aiData = dataProvider?.Ai?.[0];

            if (aiData) {
                const language = aiData.$?.language || 'English';
                const prompt = aiData.Prompt?.[0] || aiData["Description"]?.[0] || '';
                const fields = aiData.Fields?.[0]?.Field || [];

                // Find title and description fields
                const titleField = fields.find(f => f.$.name === 'title');
                const descField = fields.find(f => f.$.name === 'description');

                // Set form values for AI feed fields - use aiFeedForm directly
                if (this.aiFeedForm?.form) {
                    this.aiFeedForm.form.patchValue({
                        aiFeedLanguage: language,
                        requestFeed: prompt,
                        aiFeedTitle: titleField?.$?.description || '',
                        aiFeedDescription: descField?.$?.description || ''
                    }, { emitEvent: false });
                }
            }
        }

        // CRITICAL: Update visible fields one more time after store updates
        // This ensures form fields are properly hidden/shown after the form is fully rendered
        // This fixes the issue where updateVisibleFields() doesn't work on initial load
        this.updateVisibleFields();
        this.cdr.detectChanges();
    }

    override initRegisterDynamic() {
        // Feed Source Configuration - Store full configuration
        this.feedConfigInitFull = [
            {
                type: "select",
                label: this.translocoService.translate('ai.feedType', {}, this.translocoService.getActiveLang()),
                name: "feedType",
                value: "Rss feed",
                options: ["Rss feed", "Embedded"]
            },
            {
                type: "select",
                label: this.translocoService.translate('general.feed', {}, this.translocoService.getActiveLang()),
                name: "feed",
                value: "Tech",
                options: this.rssNames,
                hideMe: false  // Will be dynamically updated based on feedType
            },
            {
                type: "input",
                value: '',
                label: this.translocoService.translate('general.url', {}, this.translocoService.getActiveLang()),
                inputType: "text",
                name: "url",
                storePath: 'Data[0].Rss[0].$.url',
                hideMe: true
            }
        ];

        // Initialize feedConfigInit with filtered fields
        this.updateVisibleFields();

        // UI Settings Configuration
        this.uiConfigInit = [
            {
                type: "radiobutton",
                label: this.translocoService.translate('general.direction', {}, this.translocoService.getActiveLang()),
                name: "vertical",
                options: ["horizontal", "vertical"],
                value: "horizon",
                storePath: 'Data[0].Rss[0].$.vertical'
            },
            {
                type: "radiobutton",
                label: this.translocoService.translate('general.textDirection', {}, this.translocoService.getActiveLang()),
                name: "rtl",
                options: ["LTR", "RTL"],
                value: "RTL",
                storePath: 'Data[0].Rss[0].$.rtl'
            },
            {
                type: "input",
                value: '1',
                label: this.translocoService.translate('general.speed', {}, this.translocoService.getActiveLang()),
                min: '1',
                max: '999',
                inputType: "number_rich",
                showManualInput: true,
                name: "speed",
                storePath: 'Data[0].Rss[0].$.speed',
                validations: [
                    {
                        name: "required",
                        validator: Validators.required,
                        message: "value required"
                    },
                    {
                        name: "pattern",
                        validator: Validators.pattern("^[0-9]+$"),
                        message: "Accept only numbers"
                    }
                ]
            },
            {
                type: "input",
                value: '30',
                label: this.translocoService.translate('general.requestDataMinutes', {}, this.translocoService.getActiveLang()),
                min: '2',
                max: '1440',
                step: '1',
                inputType: "number_rich",
                showManualInput: true,
                name: "minRefreshTime",
                storePath: 'Data[0].Rss[0].$.minRefreshTime',
                validations: [
                    {
                        name: "required",
                        validator: Validators.required,
                        message: "value required"
                    },
                    {
                        name: "pattern",
                        validator: Validators.pattern(/^\d*\.?\d*$/),
                        // validator: Validators.pattern("^[a-zA-Z]+$"),
                        message: "Accept only numbers"
                    }
                ]
            }
        ];

        // Initialize AI Feed configuration
        this.initAiFeedConfig();

        this.updateVisibleFields();
    }

    private initRssNames() {
        Object.keys(this.rssLinks).forEach(field => {
            this.rssNames.push(this.rssLinks[field]);
        });
    }

    /**
     * Update visible fields in feedConfigInit based on current feedType
     * This method filters the full field configuration and only shows relevant fields
     * feedConfigInit now only contains feedType selector
     * rssFeedDetailsInit contains feed dropdown and url field (shown in separate section)
     */
    private updateVisibleFields() {
        // Create a complete copy of all fields with proper hideMe flags
        const feedTypeField = {...this.feedConfigInitFull.find(f => f.name === 'feedType')};
        const feedField = {...this.feedConfigInitFull.find(f => f.name === 'feed')};
        const urlField = {...this.feedConfigInitFull.find(f => f.name === 'url')};

        // feedConfigInit now only contains the feedType selector
        feedTypeField.hideMe = false;
        this.feedConfigInit = [feedTypeField];

        // rssFeedDetailsInit contains feed dropdown and url field
        feedField.hideMe = false;
        urlField.hideMe = false;
        this.rssFeedDetailsInit = [feedField, urlField];

        this.cdr.detectChanges();

        // CRITICAL: Also update the form fields directly if form is already initialized
        // This ensures the UI reflects the visibility changes immediately
        // Use a longer delay to ensure form is rendered, especially on initial load
        setTimeout(() => {
            if (this.feedForm?.getFields) {
                const fields = this.feedForm.getFields();
                if (fields && fields.length > 0) {
                    fields.forEach((k) => {
                        if (k.field.name === 'feed') {
                            k.componentRef.instance.hideMe = feedField.hideMe;
                            k.field.hideMe = feedField.hideMe;
                        } else if (k.field.name === 'url') {
                            k.componentRef.instance.hideMe = urlField.hideMe;
                            k.field.hideMe = urlField.hideMe;
                        }
                    });
                    this.cdr.detectChanges();
                }
            }
        }, 100);
    }

    /**
     * Helper method to set values in feedConfigInitFull, uiConfigInit, or aiFeedConfigInit
     */
    private setConfigValue(configName: 'feed' | 'ui' | 'ai' | 'both', fieldName: string, value: any) {
        const configs = configName === 'feed' ? [this.feedConfigInitFull] :
                        configName === 'ui' ? [this.uiConfigInit] :
                        configName === 'ai' ? [this.aiFeedConfigInit] :
                        [this.feedConfigInitFull, this.uiConfigInit, this.aiFeedConfigInit];

        for (const config of configs) {
            const index = config.findIndex((v) => v.name === fieldName);
            if (index !== -1) {
                config[index].value = value;
                return;
            }
        }
    }

    /**
     * Helper method to hide/show fields in feedConfigInit, uiConfigInit, or aiFeedConfigInit
     */
    private setConfigHide(configName: 'feed' | 'ui' | 'ai' | 'both', fieldName: string, hide: boolean) {
        const configs = configName === 'feed' ? [this.feedConfigInit] :
                        configName === 'ui' ? [this.uiConfigInit] :
                        configName === 'ai' ? [this.aiFeedConfigInit] :
                        [this.feedConfigInit, this.uiConfigInit, this.aiFeedConfigInit];

        for (const config of configs) {
            const index = config.findIndex((v) => v.name === fieldName);
            if (index !== -1) {
                config[index].hideMe = hide;

                // Update the appropriate form instance
                const targetForm = configName === 'feed' ? this.feedForm : this.form;

                if (targetForm?.getFields) {
                    targetForm.getFields().forEach((k) => {
                        if (k.field.name === fieldName) {
                            k.componentRef.instance.hideMe = hide;
                            k.field.hideMe = hide;
                        }
                    });
                }

                // Trigger change detection
                this.cdr.detectChanges();
                return;
            }
        }
    }

    override submit(formValues) {

        // CRITICAL: Preserve UI settings when switching feed types
        // If the submitted form doesn't include UI settings (vertical, rtl, speed, minRefreshTime),
        // read them from the current store and add them to formValues
        if (!formValues.hasOwnProperty('vertical')) {
            const currentVertical = this.sceneItem?.Data?.[0]?.Rss?.[0]?.$.vertical;
            if (currentVertical !== undefined) {
                // Convert stored value to UI value (radiobutton format)
                const verticalOptions = ['horizontal', 'vertical'];
                formValues.vertical = verticalOptions[currentVertical] || 'horizontal';
            }
        }
        if (!formValues.hasOwnProperty('rtl')) {
            const currentRtl = this.sceneItem?.Data?.[0]?.Rss?.[0]?.$.rtl;
            if (currentRtl !== undefined) {
                const rtlOptions = ['LTR', 'RTL'];
                formValues.rtl = rtlOptions[currentRtl] || 'LTR';
            }
        }
        if (!formValues.hasOwnProperty('speed')) {
            const currentSpeed = this.sceneItem?.Data?.[0]?.Rss?.[0]?.$.speed;
            if (currentSpeed !== undefined) {
                formValues.speed = currentSpeed;
            }
        }
        if (!formValues.hasOwnProperty('minRefreshTime')) {
            const currentMinRefreshTime = this.sceneItem?.Data?.[0]?.Rss?.[0]?.$.minRefreshTime;
            if (currentMinRefreshTime !== undefined) {
                formValues.minRefreshTime = currentMinRefreshTime;
            }
        }

        // Convert UI dropdown value to internal feedType IMMEDIATELY (before callback)
        // If formValues.feedType is null/undefined, preserve the current feedType
        const feedTypeValue = formValues.feedType || this.feedTypeToUiValue(this.feedType);
        this.feedType = this.uiValueToFeedType(feedTypeValue);

        // Update visible fields based on feedType IMMEDIATELY
        this.updateVisibleFields();

        // Trigger change detection to update isAiFeedMode() visibility
        this.cdr.detectChanges();

        return super.submit(formValues, (values: Array<ISceneItemNewValues>) => {
            const rssEmbedded = this.sceneItem?.Data?.[0]?.Rss?.[0]?.rss?.[0]?.channel?.[0]?.item;

            if (this.feedType === 'embedded' && !rssEmbedded) {
                // Switching TO embedded mode - initialize embedded data structure
                this.storeSelector.dispatch(actionUpdateSceneItem({
                        sceneId: this.sceneId,
                        sceneItemId: this.sceneItem.$.id,
                        values: [{
                            key: 'rss',
                            value: values,
                            storePath: 'Data[0].Rss[0]',
                            preProcessFunction: (sceneItemNewValues, draft, action, sceneTreeSlice) => {
                                const path = sceneTreeSlice.sceneItem?.Data?.[0]?.Rss?.[0]?.$;
                                if (path) {
                                    delete path['url'];
                                    delete path['minRefreshTime'];
                                }
                                const entry = {title: [this.defaultValue], description: [this.defaultValue]};
                                const newValue: any = [{channel: [{item: [entry]}]}];
                                const path2 = sceneTreeSlice.sceneItem?.Data?.[0]?.Rss?.[0];
                                path2.rss = newValue;
                                const urlIdx = values.findIndex((v) => v.key === 'url');
                                const minRefreshIdx = values.findIndex((v) => v.key === 'minRefreshTime');
                                if (urlIdx !== -1) values.splice(urlIdx, 1);
                                if (minRefreshIdx !== -1) values.splice(minRefreshIdx, 1);
                            }
                        }]
                    })
                );
                this.storeSelector.dispatch({type: 'BREAK_MERGE'});

                // Update the embedded data source to show the table
                this.rssEmbeddedDataSource = [{title: [this.defaultValue], description: [this.defaultValue]}];
                this.group.get('rssEmbeddedTableControl').setValue(this.rssEmbeddedDataSource);
            }

            /** switching the Store JSON structure to -> default / non embedded RSS **/
            if (rssEmbedded && this.feedType !== 'embedded') {
                this.storeSelector.dispatch(actionUpdateSceneItem({
                        sceneId: this.sceneId,
                        sceneItemId: this.sceneItem.$.id,
                        values: [{
                            key: 'rss',
                            value: values,
                            storePath: 'Data[0].Rss[0]',
                            preProcessFunction: (sceneItemNewValues, draft, action, sceneTreeSlice) => {
                                const path = sceneTreeSlice.sceneItem?.Data?.[0]?.Rss?.[0];
                                delete path['rss'];
                                const path2 = sceneTreeSlice.sceneItem?.Data?.[0]?.Rss?.[0]?.$;
                                path2.url = '';
                                path2.minRefreshTime = '1';
                            }
                        }]
                    })
                );
                this.storeSelector.dispatch({type: 'BREAK_MERGE'});
            }

            // CRITICAL: If switching AWAY from AI feed, remove DataProvider.Ai structure
            const currentDataProvider = this.sceneItem?.Data?.[0]?.DataProvider?.[0];
            const hasAiData = currentDataProvider?.Ai?.[0];

            if (hasAiData && this.feedType !== 'ai_feed') {
                // Remove the entire DataProvider structure
                this.storeSelector.dispatch(actionUpdateSceneItem({
                    sceneId: this.sceneId,
                    sceneItemId: this.sceneItem.$.id,
                    values: [{
                        key: 'DataProvider[0]',
                        value: null,
                        storePath: 'Data[0]',
                        preProcessFunction: (sceneItemNewValues, draft, action, sceneTreeSlice) => {
                            // Delete the DataProvider from the scene item
                            if (sceneTreeSlice.sceneItem?.Data?.[0]?.DataProvider) {
                                delete sceneTreeSlice.sceneItem.Data[0].DataProvider;
                            }
                        }
                    }]
                }));
                this.storeSelector.dispatch({type: 'BREAK_MERGE'});
            }

            // Handle URL assignment and DataProvider structure based on feedType
            switch (this.feedType) {
                case 'embedded':
                    const urlIndex = values.findIndex((v) => v.key === 'url');
                    const minRefreshIndex = values.findIndex((v) => v.key === 'minRefreshTime');
                    if (urlIndex !== -1) values.splice(urlIndex, 1);
                    if (minRefreshIndex !== -1) values.splice(minRefreshIndex, 1);
                    break;

                case 'ai_feed':
                    if (!formValues.hasOwnProperty('aiFeedLanguage')) {
                        break;
                    }
                    // AI Feed mode - save to DataProvider.Ai structure
                    const language = formValues.aiFeedLanguage || 'English';
                    const requestFeed = formValues.requestFeed || '';
                    const titleFieldDesc = formValues.aiFeedTitle || 'Title of the item';
                    const descriptionFieldDesc = formValues.aiFeedDescription || 'Description of the item';

                    // Build the DataProvider.Ai structure matching DataProvider interface
                    const aiDataProvider = [{
                        $: {
                            eventName: '',
                            source: 'ai',
                            itemsPath: '',
                            metadata: '',
                            url: ''
                        },
                        Ai: [{
                            $: {
                                language: language
                            },
                            Prompt: [requestFeed],
                            Fields: [{
                                Field: [
                                    {
                                        $: {
                                            name: "title",
                                            description: titleFieldDesc
                                        }
                                    },
                                    {
                                        $: {
                                            name: "description",
                                            description: descriptionFieldDesc
                                        }
                                    }
                                ]
                            }]
                        }]
                    }];

                    // Add DataProvider to values array for saving
                    values.push({
                        key: 'DataProvider',
                        value: aiDataProvider,
                        storePath: 'Data[0]'
                    });
                    break; 

                case 'rss_feed':
                    if (!formValues.hasOwnProperty('feed')) {
                        break;
                    }

                    // CRITICAL: If feed is "custom", use the URL from the form (manual input)
                    // Otherwise, look up the URL from the feed dropdown selection
                    let url: string;
                    if (formValues.feed === 'custom') {
                        // Custom URL - use the URL field value, NOT the dropdown value
                        url = formValues.url;
                    } else {
                        // Predefined feed - look up URL from rssLinks
                        if (this.sceneItem?.Data?.[0]?.Rss?.[0]?.$?.url != formValues.url) {
                            url = formValues.url;
                            this.setConfigValue('feed', 'feed', 'custom');
                            if (this.rssFeedDetailsForm?.form) {
                                const rssFeedControl = this.rssFeedDetailsForm.form.get('feed');
                                rssFeedControl.setValue('custom', { emitEvent: false });
                            }

                        } else {
                            url = Object.keys(this.rssLinks).find((k) => this.rssLinks[k] === formValues.feed);
                            this.setConfigValue('feed', 'url', url);
                            if (this.rssFeedDetailsForm?.form) {
                                const rssUrlControl = this.rssFeedDetailsForm.form.get('url');
                                rssUrlControl.setValue(url, { emitEvent: false });
                            }
                        }
                    }

                    // Regular RSS feed - handle URL from dropdown selection or manual input
                    const rssUrlIndex = values.findIndex((v) => v.key === 'url');
                    if (rssUrlIndex !== -1) {
                        // URL key exists in values array - update it
                        values[rssUrlIndex].value = url;
                    } else {
                        // URL key not in values array - add it
                        values.push({
                            key: 'url',
                            value: url,
                            storePath: 'Data[0].Rss[0].$'
                        });
                    }
                    break;
            }
            return values;
        });
    }

    onEmbeddedAdded() {
        const newValues = _.cloneDeep(this.rssEmbeddedDataSource);
        newValues.push({title: [this.defaultValue], description: [this.defaultValue]});
        const changes = [];
        changes.push({
            key: 'item',
            value: [...newValues],
            storePath: 'Data[0].Rss[0].rss[0].channel[0]'
        });
        this.dispatchSceneItemChanges({
            sceneId: this.sceneId,
            sceneItemId: this.sceneItem.$.id,
            values: changes
        });
    }

    toggleDirection() {
        const vertical = this.sceneItem?.Data?.[0]?.Rss[0].$.vertical;
        if (vertical) {
            this.showDescriptionFontSelector = vertical === '1';
        }
    }

    onCallBackValue() {
        return (item, direction) => {
            switch (direction) {
                case 'IN': {
                    item.$ = {
                        title: item.title ? item.title[0] : '',
                        description: item.description ? item.description[0] : ''
                    };
                    return item;
                }
                case 'OUT': {
                    item.title = [_.isEmpty(item.$.title.trim()) ? this.defaultValue : item.$.title ];
                    item.description = [_.isEmpty(item.$.description.trim()) ? this.defaultValue : item.$.description ];
                    delete item.$;
                    return item;
                }
            }
        };
    }

    onTableUpdate(e) {
        switch (e.currentAction) {
            case 'added': {
                this.onEmbeddedAdded();
                break;
            }
            case "rename":
            case "drop": {
                this.dispatchSceneItemChanges({
                    sceneId: this.sceneId,
                    sceneItemId: this.sceneItem.$.id,
                    values: [{
                        key: 'item',
                        value: e.values,
                        storePath: 'Data[0].Rss[0].rss[0].channel[0]'
                    }]
                });
                break;
            }
            case 'removed': {
                if (!e.selected || !e.selected[0]) {
                    return;
                }

                this.dispatchSceneItemChanges({
                    sceneId: this.sceneId,
                    sceneItemId: this.sceneItem.$.id,
                    values: [{
                        key: 'item',
                        value: e.values,
                        storePath: 'Data[0].Rss[0].rss[0].channel[0]'
                    }]
                });
                break;
            }
        }
    }

    /**
     * Open AI Feed Generator Dialog
     */
    openAiFeedDialog() {
        // Get the current RSS URL if it exists
        const currentUrl = this.sceneItem?.Data?.[0]?.Rss?.[0]?.$?.url;

        // Get the current vertical direction setting ('1' = vertical, '0' = horizontal)
        const vertical = this.sceneItem?.Data?.[0]?.Rss[0].$.vertical;
        const isVertical = vertical === '1';

        // Get the current request feed (prompt) and language from DataProvider.Ai structure
        const dataProvider = this.sceneItem?.Data?.[0]?.DataProvider?.[0];
        const aiData = dataProvider?.Ai?.[0];
        const currentPrompt = aiData?.Prompt?.[0] || aiData?.["Description"]?.[0] || '';
        const currentLanguage = aiData?.$?.language || 'English'; // Language name (e.g., 'English', 'Hebrew', 'Spanish')

        const dialogRef = this.dialog.open(DialogAnyFeedComponent, {
            width: '600px',
            maxWidth: '90vw',
            panelClass: 'custom-dialog-container',
            disableClose: false,
            data: {
                currentUrl: currentUrl,      // Pass the current URL to the dialog
                isVertical: isVertical,      // Pass the current direction setting
                prompt: currentPrompt,       // Pass the current prompt/request feed
                language: currentLanguage    // Pass the current language
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.applied && result.feedUrl) {

                // User successfully generated a feed and clicked "Apply Feed"
                const feedUrl = result.feedUrl;
                const language = result.languageName || 'English';  // Language name in proper format (e.g., 'English', 'Spanish')
                const prompt = result.prompt || result.question || '';

                // 1. Update the RSS URL in the scene store
                const changes = [];
                changes.push({
                    key: 'url',
                    value: feedUrl,
                    storePath: 'Data[0].Rss[0].$'
                });

                this.dispatchSceneItemChanges({
                    sceneId: this.sceneId,
                    sceneItemId: this.sceneItem.$.id,
                    values: changes
                });

                // Use setTimeout to ensure the form is ready and all values are updated properly
                setTimeout(() => {
                    // 2. Update the feedType dropdown to "AI feed"
                    this.feedType = 'ai_feed';
                    this.setConfigValue('feed', 'feedType', 'AI feed');
                    this.setFormValue('feedType', 'AI feed');

                    // 3. Update the URL field value
                    this.setConfigValue('feed', 'url', feedUrl);
                    this.setFormValue('url', feedUrl);

                    // 4. Update visible fields to show AI feed section
                    this.updateVisibleFields();

                    // Trigger change detection to ensure AI form is rendered
                    this.cdr.detectChanges();

                    // Wait for the AI form to be available
                    const startTime = Date.now();
                    const checkForm = () => {
                        if (this.aiFeedForm) {
                            // 5. Set the language and prompt in AI Feed Configuration form
                            this.setConfigValue('ai', 'aiFeedLanguage', language);
                            this.setConfigValue('ai', 'requestFeed', prompt);

                            // Use aiFeedForm directly to update form values
                            if (this.aiFeedForm?.form) {
                                this.aiFeedForm.form.patchValue({
                                    aiFeedLanguage: language,
                                    requestFeed: prompt
                                });
                            }

                            // Trigger change detection to update the UI
                            this.cdr.detectChanges();

                            // Show success message
                            this.toastr.success(
                                this.translocoService.translate('ai.feedAppliedSuccessfully', {}, this.translocoService.getActiveLang()),
                                this.translocoService.translate('ai.success', {}, this.translocoService.getActiveLang())
                            );
                        } else if (Date.now() - startTime < 2000) {
                            // If the form is not available, wait and try again
                            setTimeout(checkForm, 100);
                        }
                    };
                    checkForm();
                }, 100);
            }
        });
    }

    /**
     * Check if the current RSS feed is an AI-generated feed
     * Based on DataProvider.Ai existence
     */
    isAiFeed(): boolean {
        return this.feedType === 'ai_feed';
    }

    /**
     * Check if we're in embedded RSS mode
     */
    isEmbeddedMode(): boolean {
        return this.feedType === 'embedded';
    }

    /**
     * Check if we're in RSS feed mode (for UI visibility)
     */
    isRssFeedMode(): boolean {
        return this.feedType === 'rss_feed';
    }

    /**
     * Check if we're in AI feed mode (for UI visibility)
     */
    isAiFeedMode(): boolean {
        return this.feedType === 'ai_feed';
    }

    /**
     * Initialize AI Feed configuration fields
     */
    private initAiFeedConfig() {
        this.aiFeedConfigInit = [
            {
                type: "select",
                label: this.translocoService.translate('ai.language', {}, this.translocoService.getActiveLang()),
                name: "aiFeedLanguage",
                value: "English",
                options: Languages
            },
            {
                type: "input",
                value: '',
                label: this.translocoService.translate('ai.requestFeed', {}, this.translocoService.getActiveLang()),
                inputType: "textarea",
                name: "requestFeed",
                placeholder: this.translocoService.translate('ai.typeYourQuestion', {}, this.translocoService.getActiveLang())
            },
            {
                type: "input",
                value: '',
                label: this.translocoService.translate('general.title', {}, this.translocoService.getActiveLang()),
                inputType: "text",
                name: "aiFeedTitle",
                hideMe: true  // Hide in UI but still set in data structure
            },
            {
                type: "input",
                value: '',
                label: this.translocoService.translate('general.description', {}, this.translocoService.getActiveLang()),
                inputType: "text",
                name: "aiFeedDescription",
                hideMe: true  // Hide in UI but still set in data structure
            }
        ];
    }

    /**
     * Handle display mode toggle change
     * 0 = AI Feed mode, 1 = News Feed mode
     */
    onDisplayModeChange(event: any) {
        this.m_selectedDisplayMode = event.value;
        console.log('Display mode changed to:', this.m_selectedDisplayMode);

        // Automatically switch feed type based on tab selection
        if (this.m_selectedDisplayMode === 0) {
            // AI Feed tab selected - switch to AI feed mode
            this.feedType = 'ai_feed';

            // Simulate form submission with feedType = "AI feed"
            // This will trigger the same logic as selecting "AI feed" from the dropdown
            const formValues = {
                feedType: 'AI feed'
            };

            this.submit(formValues);
        } else if (this.m_selectedDisplayMode === 1) {
            // News Feed tab selected - switch to RSS feed mode
            this.feedType = 'rss_feed';

            // Set the feedType dropdown to "Rss feed" (first option)
            this.setConfigValue('feed', 'feedType', 'Rss feed');

            // Also update the form value if form is already initialized
            setTimeout(() => {
                if (this.feedForm?.form) {
                    const feedTypeControl = this.feedForm.form.get('feedType');
                    if (feedTypeControl) {
                        feedTypeControl.setValue('Rss feed', { emitEvent: false });
                    }
                }
            }, 0);

            // Simulate form submission with feedType = "Rss feed"
            const formValues = {
                feedType: 'Rss feed'
            };

            this.submit(formValues);
        }

        // Update visible fields
        this.updateVisibleFields();
        this.cdr.detectChanges();
    }
}

